import { createSelector } from '@reduxjs/toolkit';
import I18n from 'i18n-js';

import { DAILY_CLAIM, EXPLORATION } from '@lootboy/lib/adPlacementTypes';
import { authSelector, languagePlainSelector } from '@lootboy/lib/selectors';
import { cardsItemsSelector } from '@lootboy/lib/slices/cards';
import { createTutorialsHook } from '@lootboy/lib/slices/tutorials';

export const languageSelector = createSelector(
  languagePlainSelector,
  (language) => (language || I18n.locale).slice(0, 2),
);

export const dailySelector = ({ daily: { items, ...rest } }) => ({
  items: items.filter(({ platforms: { web = false } = {} }) => web),
  ...rest,
});

export const offerSelector = ({ offers: { items } }) => items || [];

export const dailyQuestSelector = createSelector(
  dailySelector,
  ({ items: daily }) => daily.some(({ type }) => type === DAILY_CLAIM),
);

export const offerCountSelector = createSelector(
  dailySelector,
  offerSelector,
  ({ items: daily }, offers) =>
    daily.some(({ type }) => type === EXPLORATION)
      ? offers.filter(({ completed }) => !completed).length
      : 0,
);

export const dailyBoostCountSelector = createSelector(
  offerCountSelector,
  (offersCount) => offersCount,
);

export const geoCountryPlainSelector = createSelector(
  authSelector,
  ({ country }) => country,
);

export const geoCountrySelector = createSelector(
  authSelector,
  ({ country }) => country?.value,
);

export const soundSelector = ({ sound }) => sound;
export const muteSelector = createSelector(soundSelector, ({ mute }) => mute);

export const useTutorials = createTutorialsHook(languageSelector, 'web');

export const evalIsUserSubscribed = (user) =>
  Boolean(
    user?.subscriptionValidUntil &&
      new Date(user.subscriptionValidUntil) > Date.now(),
  );

// todo: is this needed or is it sufficient to use sortedCardsSelector?
export const inventoryCardsSelector = createSelector(
  cardsItemsSelector,
  (items) =>
    Object.values(items).filter(
      (card) =>
        card?.cardTemplate &&
        !card.cardTemplate.hideInInventory &&
        !card.shredded,
    ),
);

export const youtubeCookieConsentSelector = ({ misc }) =>
  misc.youtubeCookiesAccepted;

export const subscriptionSelector = createSelector(
  ({ subscription }) => subscription,
  ({
    data: subscription = {} || {},
    fetchingInProgressSince,
    lastSuccessfulFetch,
  }) => {
    const loading = fetchingInProgressSince || !lastSuccessfulFetch;

    return {
      subscription: subscription || {},
      loading,
    };
  },
);

export const miscSelector = ({ misc }) => misc;

export const configurationPlainSelector = ({ configuration }) => configuration;

/**
 *
 * @returns {boolean}
 */
export const chatRadarEnabledSelector = createSelector(
  configurationPlainSelector,
  ({ data: config }) => {
    return !!config?.isChatRadarEnabled;
  },
);

export const legalDataSelectorWithInvalidate = ({
  legal: {
    fetchingInProgressSince,
    lastSuccessfulFetch,
    data,
    consentedAt,
    consentConsumed,
    didInvalidate,
  },
}) => ({
  ...data,
  loading: !!fetchingInProgressSince || !lastSuccessfulFetch,
  consentedAt,
  consentConsumed,
  didInvalidate,
});

/**
 * @return {string|null}
 */

export * from '@lootboy/lib/selectors';
